/**
 * SiteFloorPopoverButton
 *
 * A compact pill button that shows the currently selected site › floor.
 * Clicking it opens a floating Popover containing the full SiteFloorTree.
 * Used in the app toolbar so every /location/* page gets the
 * selector without consuming a permanent sidebar column.
 */
import React, { useState } from 'react';
import { Box, Popover } from '@mui/material';
import { AppButton } from '../design-system';
import { ExpandMore, Layers as LayersIcon } from '@mui/icons-material';
import { useLocationContext } from '../../contexts/LocationContext';
import SiteFloorTree from './SiteFloorTree';

const SiteFloorPopoverButton: React.FC = () => {
  const { selectedSiteId, selectedFloorId, sites, floorPlans } = useLocationContext();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const selectedSite = sites.find((s) => s.siteId === selectedSiteId);
  const selectedFloor = floorPlans.find((f) => f.floorId === selectedFloorId);

  const label = selectedSiteId
    ? `${selectedSite?.displayName ?? selectedSiteId}${selectedFloor ? ` › ${selectedFloor.displayName}` : ''}`
    : 'Select Site & Floor';

  const open = Boolean(anchorEl);

  return (
    <>
      <AppButton
        variant="outlined"
        startIcon={<LayersIcon fontSize="small" />}
        endIcon={<ExpandMore fontSize="small" sx={{ ml: -0.5 }} />}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{
          textTransform: 'none',
          fontWeight: selectedSiteId ? 600 : 400,
          color: selectedSiteId ? 'text.primary' : 'text.secondary',
          borderColor: open ? 'primary.main' : 'divider',
          maxWidth: 360,
          overflow: 'hidden',
          '& .MuiButton-startIcon': { flexShrink: 0 },
        }}
      >
        <Box
          component="span"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {label}
        </Box>
      </AppButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              width: 280,
              maxHeight: 420,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              mt: 0.5,
            },
          },
        }}
      >
        <SiteFloorTree
          popoverMode
          onFloorSelected={() => setAnchorEl(null)}
        />
      </Popover>
    </>
  );
};

export default SiteFloorPopoverButton;
